import { Block } from 'baseui/block';
import { Button } from 'baseui/button';
import { StyledBody } from 'baseui/card';
import { StatefulCheckbox } from 'baseui/checkbox';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { ALIGNMENT, BEHAVIOR, Cell, Grid } from 'baseui/layout-grid';
import { h, Fragment } from 'preact';
import { useMemo, useState } from 'preact/hooks';

import { preventDefault } from '../utils/funcs';
import ContentCard from './ContentCard';
import { usePointsOfInterest } from './map/PointsOfInterestProvider';

import Slider from './Slider';

export type Preferences = {
    chains?: 'on';
    'from': string;
    stops: '1' | '2' | '3' | '4' | '5';
    to: string;
    wifi?: 'on';
}

export default function PreferencesForm({ onSubmit }: { onSubmit: (data: FormData) => void; }) {
    const poi = usePointsOfInterest();

    const canSubmit = useMemo(() => Boolean(poi.user.center && poi.user.radius), [poi.user.center, poi.user.radius]);
    const [t0, setT0] = useState(null);
    const [t1, setT1] = useState('');

    // TODO: add explanatory tooltip to "Include chains?" question
    return ( /* eslint-disable @typescript-eslint/ban-ts-comment */
        <ContentCard title="Tell us about your Coffee Route preferences">
            <StyledBody>
                <form onSubmit={preventDefault($e => onSubmit(new FormData($e.currentTarget)))}>
                    <FormControl label={() => 'How many stops?'}>
                        <Slider initialState={{value: [1]}}
                                marks
                                max={5}
                                min={1}
                                name="stops"
                                persistentThumb
                                step={1}
                                overrides={{
                                    InnerThumb: ({$thumbIndex, $value}) => (<>{$value[$thumbIndex]}</>),
                                    ThumbValue: () => null,
                                    Thumb: {
                                        style: ({ $theme }) => ({
                                            alignItems: 'center',
                                            color: $theme.colors.contentOnColor,
                                            display: 'flex',
                                            justifyContent: 'center',
                                        })
                                    }
                                }} />
                    </FormControl>
                    <FormControl label={() => 'When do you want to be working?'}>
                        <Block display="flex" flexWrap gridGap="0.5rem" justifyContent="center">
                            <Input type={t0 == null ? 'text' : 'time'}
                                   clearable
                                   clearOnEscape // @ts-ignore
                                   max="23:59"   // @ts-ignore
                                   min="00:00"
                                   name="from"
                                   onBlur={() => setT0(prev => prev.trim() || null)}
                                   onChange={e => setT0(e.currentTarget.value)}
                                   onFocus={() => setT0(prev => prev || '')}
                                   placeholder="now"
                                   startEnhancer={() => <span>from</span>}
                                   value={t0}
                                   overrides={{
                                       Root: { style: { maxWidth: 'max-content' } },
                                       Input: { props: { size: 3 } },
                                   }} />
                            <Input type="time"
                                   clearable
                                   clearOnEscape // @ts-ignore
                                   max="23:59"   // @ts-ignore
                                   min="00:00"
                                   name="to"
                                   onChange={e => setT1(e.currentTarget.value)}
                                   required
                                   startEnhancer={() => <span style={{ textAlign: 'right', width: '4ch' }}>to</span>}
                                   value={t1}
                                   overrides={{ Root: { style: { maxWidth: 'max-content' } } }} />
                        </Block>
                    </FormControl>
                    <Grid align={ALIGNMENT.center} behavior={BEHAVIOR.fluid} gridMargins={0} overrides={{
                        Grid: { style: () => ({ alignContent: 'space-between' }) },
                        GridWrapper: { style: () => ({ height: '100%' }) }
                    }}>
                        <Cell span={[2, 4, 6]}>
                            <FormControl label={() => 'Must have wifi?'}>
                                <StatefulCheckbox initialState={{ checked: true }} name="wifi">wifi</StatefulCheckbox>
                            </FormControl>
                        </Cell>
                        <Cell span={[2, 4, 6]}>
                            <FormControl label={() => 'Include chains?'}>
                                <StatefulCheckbox initialState={{ checked: true }} name="chains">chains</StatefulCheckbox>
                            </FormControl>
                        </Cell>
                    </Grid>
                    <Button disabled={!canSubmit || !t1} type="submit">Plan a route</Button>
                </form>
            </StyledBody>
        </ContentCard>
    ); /* eslint-enable @typescript-eslint/ban-ts-comment */
}
